import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { problemsAPI } from '../utils/api';
import { resolveErrorMessage, rawErrorDetail } from '../utils/errorMessage';
import ImageCropStep from './ImageCropStep';
import FuriganaText from './FuriganaText';
import { useFuriganaAria } from '../contexts/FuriganaContext';
import { logClientError } from '../utils/safeLog';

// A crop step only makes sense for raster images — PDFs are handled server-side
// (first page → PNG) and have no meaningful in-browser preview to crop.
function isPdfFile(file) {
  if (!file) return false;
  return (
    file.type === 'application/pdf' ||
    (file.name || '').toLowerCase().endsWith('.pdf')
  );
}

function GraphUploader({ studentId, onGraphUploaded }) {
  const { t } = useTranslation();
  const fa = useFuriganaAria();
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [showRetryPrompt, setShowRetryPrompt] = useState(false);
  const [problemText, setProblemText] = useState('');
  // When true, the interactive crop tool is shown instead of the crop prompt.
  const [cropMode, setCropMode] = useState(false);

  const readPreview = (file) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setError(null);
      setShowRetryPrompt(false);
      setCropMode(false);

      // 生成预览
      readPreview(file);
    }
  };

  // Crop applied: swap in the cropped bytes as the file that will be uploaded,
  // refresh the preview, and close the crop tool.
  const handleCropped = (croppedFile) => {
    setSelectedFile(croppedFile);
    setCropMode(false);
    readPreview(croppedFile);
  };

  const resetSelection = () => {
    setSelectedFile(null);
    setPreview(null);
    setCropMode(false);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setError(null);
    setShowRetryPrompt(false);

    try {
      const response = await problemsAPI.upload({
        file: selectedFile,
        problemText: problemText.trim() ? problemText.trim() : null,
      });
      onGraphUploaded(response.data?.data || response.data);
    } catch (error) {
      logClientError('Upload failed:', error);
      // Timeout signal keys off the raw backend message (read string-safely from
      // either a legacy string detail or the structured {message}); never rendered.
      const exceedThreeMinutes = rawErrorDetail(error).includes('3分以上');
      setShowRetryPrompt(exceedThreeMinutes);
      // Inline banner keeps the generic localized key (re-localizes on switch);
      // the alert shows the specific localized error when the backend sent a code.
      setError('upload.failed');
      alert(resolveErrorMessage(error, t, 'upload.failed'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="card mx-auto max-w-3xl rounded-[32px] p-10 backdrop-blur">
      <h2 className="text-3xl font-bold" style={{ color: 'var(--text)' }}>
        {t('upload.title')}
      </h2>

      <div className="dropzone mt-8 rounded-[28px] p-10 shadow-inner">
        {!preview ? (
          <div>
            <svg
              className="mx-auto h-14 w-14"
              style={{ color: 'var(--primary-light)' }}
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <div className="mt-6">
              <label htmlFor="file-upload" className="inline-flex cursor-pointer flex-col items-center gap-2">
                <span className="rounded-full px-6 py-3 text-sm font-semibold shadow-md transition-transform hover:scale-[1.02]" style={{ backgroundColor: 'var(--bg-surface)', color: 'var(--primary)' }}>
                  <FuriganaText text={t('upload.selectFile')} />
                </span>
                <input
                  id="file-upload"
                  name="file-upload"
                  type="file"
                  accept="image/*,application/pdf"
                  className="sr-only"
                  onChange={handleFileSelect}
                />
              </label>
              <p className="mt-3 text-xs" style={{ color: 'var(--primary)' }}>
                <FuriganaText text={t('upload.fileHint')} />
              </p>
            </div>
          </div>
        ) : (
          <div>
            <img
              src={preview}
              alt={t('upload.previewAlt')}
              className="mx-auto max-h-64 rounded-2xl shadow-lg" style={{ border: '1px solid var(--border)' }}
            />
            <button
              onClick={resetSelection}
              className="mt-4 text-sm font-semibold transition-colors" style={{ color: 'var(--primary)' }}
            >
              <FuriganaText text={t('upload.selectAnother')} />
            </button>
          </div>
        )}
      </div>

      {preview && !isPdfFile(selectedFile) && (
        cropMode ? (
          <ImageCropStep
            file={selectedFile}
            imageSrc={preview}
            onCropped={handleCropped}
            onSkip={() => setCropMode(false)}
            t={t}
          />
        ) : (
          <div
            className="mt-6 rounded-[28px] p-6 shadow-sm"
            style={{ border: '1px solid var(--border)', backgroundColor: 'var(--warm-bg)' }}
          >
            <div className="flex items-start gap-3">
              <span aria-hidden="true" className="text-2xl leading-none">🛡️</span>
              <div className="flex-1">
                <h3 className="text-lg font-bold" style={{ color: 'var(--text)' }}>
                  <FuriganaText text={t('upload.cropPromptTitle')} />
                </h3>
                <p className="mt-1 text-sm leading-relaxed" style={{ color: 'var(--text-muted)' }}>
                  <FuriganaText text={t('upload.cropPromptBody')} />
                </p>
              </div>
            </div>
            <div className="mt-4">
              <button
                type="button"
                onClick={() => setCropMode(true)}
                className="btn-primary rounded-full px-6 py-3 text-sm font-semibold shadow-md transition-transform hover:scale-[1.01]"
              >
                <FuriganaText>✂️ {t('upload.cropButton')}</FuriganaText>
              </button>
            </div>
          </div>
        )
      )}

      <div className="mt-8 grid gap-3">
        <label htmlFor="problem-text" className="text-sm font-semibold" style={{ color: 'var(--text)' }}>
          <FuriganaText text={t('upload.problemTextLabel')} />
        </label>
        <textarea
          id="problem-text"
          value={problemText}
          onChange={(e) => setProblemText(e.target.value)}
          placeholder={fa(t('upload.problemTextPlaceholder'))}
          className="input-field min-h-[96px] w-full rounded-2xl px-4 py-3 text-sm shadow-inner"
        />
      </div>

      {/* Hide the upload button while the crop tool is open: selectedFile is still the ORIGINAL
          until the student applies/skips the crop, so an early Upload would post the un-cropped
          image and bypass the client-side privacy layer (Codex review 2026-07-10, P2). */}
      {selectedFile && !cropMode && (
        <div className="mt-8">
          <button
            onClick={handleUpload}
            disabled={uploading}
            className="btn-primary w-full rounded-full py-3 text-lg shadow-md transition-transform hover:scale-[1.01] disabled:scale-100"
          >
            <FuriganaText text={uploading ? t('upload.uploading') : t('upload.submit')} />
          </button>
        </div>
      )}

      {error && (
        <p className="mt-4 text-center text-sm" role="alert" style={{ color: 'var(--danger)' }}>
          <FuriganaText text={t(error)} />
        </p>
      )}
      {showRetryPrompt && (
        <div className="mt-3 rounded-2xl p-4 text-sm shadow-sm" style={{ border: '1px solid var(--border)', backgroundColor: 'var(--warm-bg)', color: 'var(--text)' }}>
          <div className="font-semibold"><FuriganaText text={t('upload.timeoutTitle')} /></div>
          <p className="mt-1 text-xs" style={{ color: 'var(--text-muted)' }}>
            <FuriganaText text={t('upload.timeoutBody')} />
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              onClick={handleUpload}
              disabled={uploading}
              className="btn-primary rounded-full px-4 py-1.5 text-xs shadow" style={{ backgroundColor: 'var(--warning)', color: 'var(--text-on-primary)' }}
            >
              <FuriganaText text={t('upload.retry')} />
            </button>
            <button
              onClick={() => setShowRetryPrompt(false)}
              className="btn-secondary rounded-full px-4 py-1.5 text-xs"
            >
              {t('upload.later')}
            </button>
          </div>
        </div>
      )}

      <div className="mt-8 rounded-2xl p-6" style={{ border: '1px solid var(--border)', backgroundColor: 'var(--primary-bg)' }}>
        <h3 className="text-sm font-semibold" style={{ color: 'var(--primary)' }}>
          <FuriganaText>💡 {t('upload.supportedTypesTitle')}</FuriganaText>
        </h3>
        <ul className="mt-3 space-y-1 text-sm" style={{ color: 'var(--text-muted)' }}>
          <li><FuriganaText text={t('upload.types1')} /></li>
          <li><FuriganaText text={t('upload.types2')} /></li>
          <li><FuriganaText text={t('upload.types3')} /></li>
          <li><FuriganaText text={t('upload.types4')} /></li>
        </ul>
        <p className="mt-3 text-xs leading-relaxed" style={{ color: 'var(--text-muted)' }}>
          <FuriganaText text={t('upload.blurNote')} /><br />
            
        </p>
      </div>
    </div>
  );
}

export default GraphUploader;
